import { useI18n } from 'vue-i18n'
import { formatDate, formatTime } from '~/utils/dateHelpers'
import type { EventDate, EventDateChildren } from '~/composables/types/event'
import type { CartItem } from '~/composables/types/cart'

export const useEventDateHelpers = () => {
  const { locale } = useI18n()

  type GeneralDate = EventDate | CartItem | EventDateChildren

  const fnGetDateLabelHtml = (
    date: GeneralDate,
    currentDate = new Date()
  ): string => {
    if (typeof currentDate === 'string') {
      currentDate = new Date(currentDate)
    }

    const start = date?.start || date?.startTime
    const end = date?.end || date?.endTime

    if (!start || !end) {
      return ''
    }

    const eventDate = new Date(start)
    const dateStr = formatDate(start, locale.value)

    // Check if all_day exists and is true (only EventDate has this property)
    const all_day = 'all_day' in date && date.all_day

    // Check if the event is more than 12 months after the current date
    const twelveMonthsLater = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 12,
      1
    )

    const shouldIncludeYear = eventDate > twelveMonthsLater
    const yearPart = shouldIncludeYear ? ` ${eventDate.getFullYear()}` : ''

    // For all-day events, only show date (no time)
    if (all_day) {
      return `${dateStr}${yearPart}`
    }

    // For regular events, show date and time
    const startTimeStr = formatTime(start)
    const endTimeStr = formatTime(end)
    return `${dateStr}${yearPart} ${startTimeStr} - ${endTimeStr}`
  }

  const fnGetChildrenHtmlArr = (item: GeneralDate) => {
    return (
      item.children?.data?.map((x: EventDateChildren) =>
        fnGetDateLabelHtml(x)
      ) || []
    )
  }

  return {
    fnGetDateLabelHtml,
    fnGetChildrenHtmlArr,
  }
}

export const getDateTime = (date: EventDate): string => {
  const { t } = useI18n()

  // Check all_day first, before checking for missing values
  const all_day = date?.all_day
  if (all_day) {
    return t('product.show.checkout.allDay')
  }

  const start = date?.start
  const end = date?.end

  if (!start || !end) {
    return ''
  }

  const startTimeStr = formatTime(start)
  const endTimeStr = formatTime(end)
  return `${startTimeStr} - ${endTimeStr}`
}
